import React, { useRef, useEffect, useState, useCallback } from 'react'

const RELATIONSHIPS = [null, 'team', 'cross-team', 'skip-level', 'external']

const RELATIONSHIP_CONFIG = {
  team: { label: 'team', bg: '#e6f1fb', color: '#185FA5' },
  'cross-team': { label: 'cross-team', bg: '#FAEEDA', color: '#633806' },
  'skip-level': { label: 'skip-level', bg: '#F5E7F5', color: '#5C1F5C' },
  external: { label: 'external', bg: '#f1efe8', color: '#444441' },
}

function nextRelationship(current) {
  const idx = RELATIONSHIPS.indexOf(current)
  return RELATIONSHIPS[(idx + 1) % RELATIONSHIPS.length]
}

function generateId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return `k_${crypto.randomUUID().slice(0, 8)}`
  }
  return `k_${Math.random().toString(36).slice(2, 10)}`
}

function autoResize(el) {
  if (!el) return
  el.style.height = 'auto'
  el.style.height = el.scrollHeight + 'px'
}

function RelationshipChip({ value, onCycle, disabled }) {
  if (value === null) {
    return (
      <button
        type="button"
        onClick={onCycle}
        disabled={disabled}
        style={{
          fontSize: 9, fontWeight: 500, padding: '2px 6px',
          background: 'transparent', color: '#8A8780',
          border: '0.5px dashed #c8ccd4', borderRadius: 4,
          textTransform: 'uppercase', letterSpacing: '0.03em',
          cursor: disabled ? 'default' : 'pointer',
          flexShrink: 0, fontFamily: 'inherit',
        }}
        aria-label="Add relationship"
      >
        + tag
      </button>
    )
  }

  const config = RELATIONSHIP_CONFIG[value]
  return (
    <button
      type="button"
      onClick={onCycle}
      disabled={disabled}
      style={{
        fontSize: 9, fontWeight: 500, padding: '2px 6px',
        background: config.bg, color: config.color,
        border: 'none', borderRadius: 4,
        textTransform: 'uppercase', letterSpacing: '0.03em',
        cursor: disabled ? 'default' : 'pointer',
        flexShrink: 0, fontFamily: 'inherit',
      }}
      aria-label={`Relationship: ${config.label}. Click to change.`}
    >
      {config.label}
    </button>
  )
}

function DeleteButton({ onClick, disabled, visible }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        marginLeft: 'auto', padding: '2px 4px',
        background: 'none', border: 'none',
        cursor: disabled ? 'default' : 'pointer',
        color: 'var(--text-secondary)', fontSize: 14, lineHeight: 1,
        flexShrink: 0, opacity: visible ? 0.6 : 0,
        transition: 'opacity var(--duration-fast) ease',
      }}
      aria-label="Delete kudos"
    >
      ×
    </button>
  )
}

function DescriptionInput({ value, placeholder, onChange, disabled }) {
  const ref = useRef(null)

  useEffect(() => {
    autoResize(ref.current)
  }, [value])

  return (
    <textarea
      ref={ref}
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      onInput={(e) => autoResize(e.target)}
      disabled={disabled}
      rows={1}
      style={{
        width: '100%', fontSize: 12,
        color: 'var(--text-secondary)', lineHeight: 1.4,
        background: 'transparent', border: 'none', outline: 'none',
        resize: 'none', padding: 0, overflowY: 'hidden',
        minHeight: '17px', fontFamily: 'inherit',
      }}
      aria-label="What they did"
    />
  )
}

function KudosRow({ item, onChange, onDelete, disabled }) {
  const [hovered, setHovered] = useState(false)

  return (
    <div
      style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      role="group"
      aria-label={`Kudos for ${item.name || 'unnamed person'}`}
    >
      <div
        style={{
          width: 4, borderRadius: 2, background: '#e0ddd4',
          flexShrink: 0, alignSelf: 'stretch',
        }}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3, minWidth: 0 }}>
          <input
            type="text"
            value={item.name}
            placeholder="Who?"
            onChange={(e) => onChange({ ...item, name: e.target.value })}
            disabled={disabled}
            style={{
              fontSize: 13, fontWeight: 600,
              color: 'var(--text-primary)', background: 'transparent',
              border: 'none', outline: 'none', padding: 0,
              fontFamily: 'inherit', minWidth: 0, flex: '0 1 auto',
            }}
            aria-label="Person's name"
          />
          <RelationshipChip
            value={item.relationship}
            onCycle={() => onChange({ ...item, relationship: nextRelationship(item.relationship) })}
            disabled={disabled}
          />
          <DeleteButton onClick={onDelete} disabled={disabled} visible={hovered} />
        </div>
        <DescriptionInput
          value={item.description}
          placeholder="What did they do?"
          onChange={(description) => onChange({ ...item, description })}
          disabled={disabled}
        />
      </div>
    </div>
  )
}

function AddButton({ variant, onClick, label, disabled }) {
  const baseStyle = {
    fontSize: 12, fontWeight: 500, color: '#185FA5',
    background: 'none', cursor: disabled ? 'default' : 'pointer',
    fontFamily: 'inherit', padding: '6px 14px', borderRadius: 6,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    gap: 4, opacity: disabled ? 0.4 : 1, width: '100%',
  }
  const variantStyle = variant === 'solid'
    ? { border: '0.5px solid #c8d6ea' }
    : { border: '0.5px dashed #c8d6ea', padding: '6px 0' }

  return (
    <button type="button" onClick={onClick} disabled={disabled} style={{ ...baseStyle, ...variantStyle }}>
      {label}
    </button>
  )
}

function EmptyState({ onAdd, disabled }) {
  return (
    <div style={{ padding: '18px 4px 8px 4px', textAlign: 'center' }}>
      <div style={{ fontSize: 11, color: 'var(--text-secondary)', fontStyle: 'italic', marginBottom: 12 }}>
        No recognition notes yet
      </div>
      <AddButton variant="solid" onClick={onAdd} label="+ add a kudos" disabled={disabled} />
      <div style={{ fontSize: 10, color: 'var(--text-secondary)', marginTop: 8, letterSpacing: '0.02em' }}>
        Up to 2 people
      </div>
    </div>
  )
}

export function KudosList({ value, onChange, maxItems = 2, disabled = false }) {
  const items = (value?.items ?? []).slice(0, maxItems)

  const handleAdd = useCallback(() => {
    if (items.length >= maxItems) return
    const next = [...items, { id: generateId(), name: '', description: '', relationship: null }]
    onChange({ items: next })
  }, [items, maxItems, onChange])

  const handleDelete = useCallback((idx) => {
    onChange({ items: items.filter((_, i) => i !== idx) })
  }, [items, onChange])

  const handleRowChange = useCallback((idx, updated) => {
    const next = items.map((item, i) => i === idx ? updated : item)
    onChange({ items: next })
  }, [items, onChange])

  return (
    <div style={{ padding: '8px 14px 12px', display: 'flex', flexDirection: 'column', gap: 10 }}>
      {items.length === 0 ? (
        <EmptyState onAdd={handleAdd} disabled={disabled} />
      ) : (
        <>
          {items.map((item, idx) => (
            <KudosRow
              key={item.id}
              item={item}
              onChange={(next) => handleRowChange(idx, next)}
              onDelete={() => handleDelete(idx)}
              disabled={disabled}
            />
          ))}
          {items.length < maxItems && (
            <AddButton
              variant="dashed"
              onClick={handleAdd}
              label="+ add one more"
              disabled={disabled}
            />
          )}
        </>
      )}
    </div>
  )
}
